import Head from 'next/head';
import { useRouter } from 'next/router';
import { getCompanyAddressesJsonLd } from '@/constants/companyInfo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  structuredData?: object | object[];
  noindex?: boolean;
  nofollow?: boolean;
}

const defaultTitle =
  'Trường Thọ Food - Trứng Gà Tươi & Trứng Bóc Vỏ Sẵn Chất Lượng Cao';
const defaultDescription =
  'Trường Thọ Food chuyên cung cấp trứng gà tươi và trứng bóc vỏ sẵn chất lượng cao. Sản phẩm đảm bảo vệ sinh an toàn thực phẩm, giá cả cạnh tranh. Hotline: 083 419 4986';
const defaultKeywords =
  'trứng gà, trứng gà tươi, trứng bóc vỏ sẵn, trứng chế biến sẵn, nông sản, thực phẩm sạch, công ty Trường Thọ, Trường Thọ Food, trứng gà Dĩ An, trứng gà Hồ Chí Minh, trứng gà tươi số 1';
const siteUrl = 'https://www.truongthofood.vn';
const defaultImage = `${siteUrl}/logo-hori.png`;

const SEOHead = ({
  title = defaultTitle,
  description = defaultDescription,
  keywords = defaultKeywords,
  image = defaultImage,
  url,
  type = 'website',
  structuredData,
  noindex = false,
  nofollow = false,
}: SEOHeadProps) => {
  const router = useRouter();
  const currentUrl = url || `${siteUrl}${router.asPath}`;
  const fullTitle = title.includes('Trường Thọ') ? title : `${title} | Trường Thọ Food`;

  // Robots meta
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');

  // Structured data
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Trường Thọ Food',
    url: siteUrl,
    logo: `${siteUrl}/logo-hori.png`,
    description: defaultDescription,
    address: getCompanyAddressesJsonLd(),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+84-83-419-4986',
      contactType: 'customer service',
      email: 'truongthofood68@gmail.com',
      areaServed: 'VN',
      availableLanguage: 'Vietnamese',
    },
    sameAs: [],
  };

  const structuredDataArray = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [defaultStructuredData];

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robotsContent} />
      <meta name="author" content="Trường Thọ Food" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Trường Thọ Food" />
      <meta property="og:locale" content="vi_VN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#1a73e8" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=yes" />

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
    </Head>
  );
};

export default SEOHead;

